import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Send,
  FileText,
  MoreVertical,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
  ExternalLink,
  Receipt,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVOICE_TYPES = [
  { value: "single_day", label: "Single Day Training", defaultAmount: 110 },
  { value: "four_week_block", label: "4-Week Training Block", defaultAmount: 440 }
];

export const InvoicesTab = ({ clients = [] }) => {
  const { getAuthHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Create invoice form
  const [formData, setFormData] = useState({
    invoice_type: "single_day",
    amount: 110,
    recipient_email: "",
    recipient_name: "",
    client_id: "",
    puppy_name: "",
    description: ""
  });

  // Pre-fill for existing client
  const [useExistingClient, setUseExistingClient] = useState(true);

  const fetchInvoices = useCallback(async () => {
    try {
      const query = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/invoices${query}`, {
        headers: getAuthHeaders()
      });
      setInvoices(res.data.invoices || []);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleInvoiceTypeChange = (value) => {
    const type = INVOICE_TYPES.find(t => t.value === value);
    setFormData(prev => ({
      ...prev,
      invoice_type: value,
      amount: type?.defaultAmount || prev.amount
    }));
  };

  const handleClientSelect = (clientId) => {
    if (clientId === "custom") {
      setUseExistingClient(false);
      setFormData(prev => ({
        ...prev,
        client_id: "",
        recipient_email: "",
        recipient_name: "",
        puppy_name: ""
      }));
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          client_id: clientId,
          recipient_email: client.email,
          recipient_name: client.name,
          puppy_name: client.puppies?.[0]?.name || ""
        }));
      }
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      // Remove empty optional fields
      if (!payload.client_id) delete payload.client_id;
      if (!payload.puppy_name) delete payload.puppy_name;
      if (!payload.description) delete payload.description;

      await axios.post(`${API}/invoices`, payload, {
        headers: getAuthHeaders()
      });
      
      toast.success("Invoice sent successfully!");
      setCreateOpen(false);
      resetForm();
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_type: "single_day",
      amount: 110,
      recipient_email: "",
      recipient_name: "",
      client_id: "",
      puppy_name: "",
      description: ""
    });
    setUseExistingClient(true);
  };

  const handleResend = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/resend`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Invoice resent!");
    } catch (err) {
      toast.error("Failed to resend invoice");
    }
  };

  const handleSendReceipt = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send-receipt`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Receipt sent!");
    } catch (err) {
      toast.error("Failed to send receipt");
    }
  };

  const handleMarkPaid = async (invoiceId, method) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/mark-paid?payment_method=${method}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Invoice marked as paid");
      fetchInvoices();
    } catch (err) {
      toast.error("Failed to update invoice");
    }
  };

  const handleCancel = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to cancel this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Invoice cancelled");
      fetchInvoices();
    } catch (err) {
      toast.error("Failed to cancel invoice");
    }
  };

  const copyPaymentLink = (invoiceId) => {
    const link = `${window.location.origin}/pay-invoice/${invoiceId}`;
    navigator.clipboard.writeText(link);
    toast.success("Payment link copied!");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case "sent":
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Sent</Badge>;
      case "cancelled":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-olive border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl text-charcoal">Invoices</h2>
          <p className="text-stone text-sm">Send and manage invoices for additional training</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Invoices</SelectItem>
              <SelectItem value="sent">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Invoices Table */}
      {invoices.length === 0 ? (
        <div className="text-center py-12 bg-sand/30 rounded-xl">
          <FileText className="w-12 h-12 text-stone/50 mx-auto mb-4" />
          <p className="text-stone">No invoices yet</p>
          <p className="text-sm text-stone/70 mt-1">Create your first invoice to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-olive/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-sand/30">
                <TableHead>Invoice #</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-charcoal">{invoice.recipient_name}</p>
                      <p className="text-xs text-stone">{invoice.recipient_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {invoice.invoice_type === "single_day" ? "Single Day" : "4-Week Block"}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">${invoice.amount.toFixed(2)}</TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell className="text-sm text-stone">{formatDate(invoice.sent_at)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => copyPaymentLink(invoice.id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Payment Link
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/pay-invoice/${invoice.id}`, '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Invoice
                        </DropdownMenuItem>
                        
                        {invoice.status === "sent" && (
                          <>
                            <DropdownMenuItem onClick={() => handleResend(invoice.id)}>
                              <Send className="w-4 h-4 mr-2" />
                              Resend Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMarkPaid(invoice.id, "bank_transfer")}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark as Paid (Bank)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMarkPaid(invoice.id, "cash")}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark as Paid (Cash)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCancel(invoice.id)} className="text-red-600">
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Invoice
                            </DropdownMenuItem>
                          </>
                        )}
                        
                        {invoice.status === "paid" && (
                          <DropdownMenuItem onClick={() => handleSendReceipt(invoice.id)}>
                            <Receipt className="w-4 h-4 mr-2" />
                            Resend Receipt
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Create Invoice</DialogTitle>
            <DialogDescription>
              Send an invoice for additional training services
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateInvoice} className="space-y-4">
            {/* Invoice Type */}
            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <Select value={formData.invoice_type} onValueChange={handleInvoiceTypeChange}>
                <SelectTrigger data-testid="invoice-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVOICE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label} (${type.defaultAmount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Amount (AUD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-7"
                  data-testid="invoice-amount-input"
                />
              </div>
              <p className="text-xs text-stone">Amount includes GST</p>
            </div>

            {/* Recipient Selection */}
            <div className="space-y-2">
              <Label>Send To</Label>
              {clients.length > 0 && useExistingClient ? (
                <Select onValueChange={handleClientSelect}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Select existing client or enter custom" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} ({client.email})
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Enter custom email...</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-3">
                  {clients.length > 0 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setUseExistingClient(true)}
                    >
                      ← Select existing client
                    </Button>
                  )}
                  <Input
                    placeholder="Recipient Name"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
                    required
                    data-testid="recipient-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Recipient Email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                    required
                    data-testid="recipient-email-input"
                  />
                </div>
              )}
            </div>

            {/* Puppy Name (optional) */}
            <div className="space-y-2">
              <Label>Puppy Name (optional)</Label>
              <Input
                placeholder="e.g., Max"
                value={formData.puppy_name}
                onChange={(e) => setFormData(prev => ({ ...prev, puppy_name: e.target.value }))}
                data-testid="puppy-name-input"
              />
            </div>

            {/* Description (optional) */}
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Additional details about the training..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                data-testid="invoice-description-input"
              />
            </div>

            {/* Summary */}
            {formData.recipient_email && (
              <div className="bg-sand/50 rounded-lg p-4">
                <p className="text-sm text-stone mb-1">Invoice will be sent to:</p>
                <p className="font-medium text-charcoal">{formData.recipient_name || 'Name required'}</p>
                <p className="text-sm text-stone">{formData.recipient_email}</p>
                <p className="font-heading text-xl text-olive mt-2">${parseFloat(formData.amount || 0).toFixed(2)} AUD</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={sending || !formData.recipient_email || !formData.recipient_name}
                data-testid="send-invoice-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invoice
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesTab;
